import { useState, useEffect } from 'react';
import { AlertTriangle, Ban, Check, Eye, RefreshCw, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getSuspiciousIPs, blockIP, unblockIP, type SuspiciousIP } from '@/lib/adminApi';

export function SuspiciousIPsPanel() {
  const [suspiciousIPs, setSuspiciousIPs] = useState<SuspiciousIP[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'blocked' | 'active'>('all');
  const [blockDrawerOpen, setBlockDrawerOpen] = useState(false);
  const [selectedIP, setSelectedIP] = useState<SuspiciousIP | null>(null);
  const [blockForm, setBlockForm] = useState({
    reason: '',
    durationHours: 24,
  });

  useEffect(() => {
    loadSuspiciousIPs();
  }, [filter]);

  const loadSuspiciousIPs = async () => {
    try {
      setIsLoading(true);
      const data = await getSuspiciousIPs({ status: filter, limit: 50 });
      setSuspiciousIPs(data);
    } catch (error: unknown) {
      console.error('Failed to load suspicious IPs:', error);
      // Don't show toast for initial load failure - table may not exist yet
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlockIP = (ip: SuspiciousIP) => {
    setSelectedIP(ip);
    setBlockForm({ reason: '', durationHours: 24 });
    setBlockDrawerOpen(true);
  };

  const handleUnblockIP = async (ip: SuspiciousIP) => {
    if (!confirm(`Unblock IP ${ip.ip_address}?`)) return;

    try {
      await unblockIP(ip.ip_address);
      await loadSuspiciousIPs();
      toast.success(`IP ${ip.ip_address} unblocked`);
    } catch (error: unknown) {
      console.error('Failed to unblock IP:', error);
      toast.error((error as Error)?.message || 'Failed to unblock IP');
    }
  };

  const handleSubmitBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIP) return;

    try {
      await blockIP(selectedIP.ip_address, {
        reason: blockForm.reason || 'Blocked by admin',
        durationHours: blockForm.durationHours,
      });
      await loadSuspiciousIPs();
      setBlockDrawerOpen(false);
      toast.success(`IP ${selectedIP.ip_address} blocked`);
    } catch (error: unknown) {
      console.error('Failed to block IP:', error);
      toast.error((error as Error)?.message || 'Failed to block IP');
    }
  };

  const stats = {
    total: suspiciousIPs.length,
    blocked: suspiciousIPs.filter(ip => ip.blocked).length,
    highActivity: suspiciousIPs.filter(ip => ip.hit_count >= 5).length,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-400" />
          <h3 className="text-sm font-semibold text-white">Suspicious IPs</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={loadSuspiciousIPs}
          className="h-8"
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="flex gap-4 mb-4 text-xs">
        <span className="flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-muted-foreground">{stats.total} tracked</span>
        </span>
        <span className="flex items-center gap-1.5">
          <Ban className="w-3.5 h-3.5 text-red-400" />
          <span className="text-muted-foreground">{stats.blocked} blocked</span>
        </span>
        <span className="flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />
          <span className="text-muted-foreground">{stats.highActivity} high activity</span>
        </span>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {(['all', 'blocked', 'active'] as const).map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? 'bg-[#8FB560] hover:bg-[#7a9d51]' : 'border-border'}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* IP List */}
      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
        {isLoading ? (
          <div className="animate-pulse space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted rounded" />
            ))}
          </div>
        ) : suspiciousIPs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No suspicious IPs detected yet.
          </div>
        ) : (
          suspiciousIPs.map((ip) => (
            <div
              key={ip.ip_address}
              className={`p-3 rounded-lg border ${
                ip.blocked
                  ? 'bg-red-500/5 border-red-500/20'
                  : ip.hit_count >= 5
                    ? 'bg-yellow-500/5 border-yellow-500/20'
                    : 'bg-background border-border'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-white">{ip.ip_address}</span>
                    {ip.blocked && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                        Blocked
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    {ip.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {ip.location}
                      </span>
                    )}
                    <span>{ip.hit_count} hits</span>
                    <span>Last: {new Date(ip.last_seen).toLocaleString()}</span>
                  </div>
                  {ip.block_reason && (
                    <p className="text-xs text-red-400 mt-1">{ip.block_reason}</p>
                  )}
                </div>
                <div className="flex-shrink-0 ml-3">
                  {ip.blocked ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnblockIP(ip)}
                      className="h-8 text-green-400 hover:text-green-300"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Unblock
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleBlockIP(ip)}
                      className="h-8 text-red-400 hover:text-red-300"
                    >
                      <Ban className="w-3 h-3 mr-1" />
                      Block
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Block IP Drawer */}
      <AdminFormDrawer
        open={blockDrawerOpen}
        onClose={() => setBlockDrawerOpen(false)}
        title="Block IP Address"
        description={`Block ${selectedIP?.ip_address} from accessing the platform.`}
      >
        <form onSubmit={handleSubmitBlock} className="space-y-4">
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-400">
              Blocking this IP will immediately deny access from {selectedIP?.ip_address}.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Block Duration</Label>
            <Select
              value={blockForm.durationHours.toString()}
              onValueChange={(val) => setBlockForm({ ...blockForm, durationHours: parseInt(val) })}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 hour</SelectItem>
                <SelectItem value="6">6 hours</SelectItem>
                <SelectItem value="24">24 hours</SelectItem>
                <SelectItem value="168">1 week</SelectItem>
                <SelectItem value="720">30 days</SelectItem>
                <SelectItem value="-1">Permanent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Input
              value={blockForm.reason}
              onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
              placeholder="e.g., Suspicious login attempts"
              className="bg-background border-border"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">
              <Ban className="w-4 h-4 mr-2" />
              Block IP
            </Button>
            <Button type="button" variant="outline" onClick={() => setBlockDrawerOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>
    </div>
  );
}
